import React from 'react';
import {
  Anchor, Row, Col, Typography
} from 'antd';
import Markdown from 'react-markdown';
import { AnchorProps } from 'antd/lib/anchor';
import { useHistory } from 'react-router-dom';
import styles from './index.module.less';

const FaqAnchor: React.FC<AnchorProps & { dataSource?: FaqItem[] }> = ({
  dataSource = [],
  ...rest
}) => {
  const { createHref, location } = useHistory();
  return (
    <Anchor {...rest} className={styles.nav}>
      {dataSource.map(({ id, title }) => (
        <Anchor.Link
          key={id}
          href={createHref({ ...location, hash: id })}
          title={title}
        />
      ))}
    </Anchor>
  )
};

interface FaqItem {
  id: string;
  title: string;
  content: string;
}

interface FaqProps extends React.HtmlHTMLAttributes<HTMLDivElement> {
  anchor?: boolean;
  dataSource?: FaqItem[];
}

const Faq: React.FC<FaqProps> = (props) => {
  const history = useHistory();
  const ref = React.useRef<HTMLDivElement>(null);
  const { dataSource = [], anchor = false, ...rest } = props;
  const [style, setStyle] = React.useState<React.CSSProperties>({})

  const offsetTop = 88;

  const setMaxHeight = React.useCallback(
    () => {
      if (!ref.current) {
        return;
      }
      const rect = ref.current.getBoundingClientRect();
      const maxHeight = Math.max(0, window.innerHeight - offsetTop - Math.max(0, window.innerHeight - (rect.top + rect.height)));
      setStyle({ maxHeight })
    },
    []
  );

  React.useEffect(
    () => {
      setMaxHeight();
      window.addEventListener('resize', setMaxHeight);
      window.addEventListener('scroll', setMaxHeight);
      return () => {
        window.removeEventListener('scroll', setMaxHeight);
      }
    },
    [setMaxHeight]
  )

  const content = React.useMemo(
    () => (
      <>
        {dataSource.map(({ id, title, content }) => (
          <div key={id}>
            <div
              className={styles.anchor}
              id={history.createHref({ ...history.location, hash: id }).replace(/^.*?#/, '')}
            />
            <Typography.Title level={4} className={styles.title}>
              {id}
              :
              {' '}
              {title}
            </Typography.Title>
            <Markdown className={styles.content} source={content} />
          </div>
        ))}
      </>
    ),
    [dataSource, history]
  );

  if (anchor) {
    return (
      <div ref={ref} {...rest}>
        <Row gutter={32}>
          <Col xs={0} md={8} lg={6}>
            <FaqAnchor
              style={style}
              dataSource={dataSource}
              offsetTop={offsetTop}
              targetOffset={0}
            />
          </Col>
          <Col xs={24} md={0}>
            <FaqAnchor
              style={{ maxHeight: 'auto' }}
              dataSource={dataSource}
              affix={false}
            />
          </Col>
          <Col md={16} lg={18}>
            {content}
          </Col>
        </Row>
      </div>
    );
  }

  return <div {...rest}>{content}</div>;
};

export default Faq;
